import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { SumsubService } from '@/core/services/sumsubService';
import SumsubWebSdk from '@sumsub/websdk-react';
import Loading from '@/core/components/globals/loading';
import { toast } from 'react-toastify';
import { TravelRuleStatus } from '@/store/auth/enum';

interface TravelRuleModalProps {
  onClose: () => void;
  isFullModal?: boolean;
}

const TravelRuleModal: React.FC<TravelRuleModalProps> = ({ onClose, isFullModal }) => {
  const { t, i18n } = useTranslation();
  const [showWebSdk, setShowWebSdk] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [loading, setLoading] = useState<boolean>(true);
  const statusCheckTimerRef = useRef<NodeJS.Timeout | null>(null);
  const attemptCountRef = useRef(0);
  const MAX_ATTEMPTS = 120;
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [completionStatus, setCompletionStatus] = useState<'completed' | 'failed' | null>(null);

  // Fetch the travel rule status — isDeposit is derived from query data, not local state
  const { data: statusData, isLoading: isStatusLoading } = useQuery({
    queryKey: ['travelRuleStatus'],
    queryFn: SumsubService.getTravelRuleStatus,
  });

  const isDeposit = statusData?.is_deposit ?? true;

  const accessTokenExpirationHandler = async () => {
    const type = isDeposit ? 'deposit' : 'withdrawal';
    const token = (await SumsubService.getTravelRuleToken(type)).token;
    setAccessToken(token);
    setLoading(false);
  }

  const handleNext = async () => {
    setShowWebSdk(true);
    await accessTokenExpirationHandler();
  };

  const timerGetTravelRuleStatus = () => {
    if (statusCheckTimerRef.current) clearTimeout(statusCheckTimerRef.current);

    attemptCountRef.current += 1;
    if (attemptCountRef.current > MAX_ATTEMPTS) {
      setIsProcessing(false);
      setCompletionStatus('failed');
      return;
    }

    // Exponential backoff: 5s, 10s, 20s, 40s, capped at 60s
    const delay = Math.min(5000 * Math.pow(2, Math.min(attemptCountRef.current - 1, 4)), 60000);

    statusCheckTimerRef.current = setTimeout(() => {
      SumsubService.getTravelRuleStatus().then((data) => {
        if (data.status === TravelRuleStatus.COMPLETED || !data.status) {
          // Successfully completed (or no pending travel rule = already processed)
          console.log('Travel rule status check completed with status:', data.status);
          statusCheckTimerRef.current = null;
          setIsProcessing(false);
          setCompletionStatus('completed');
        } else if (data.status === TravelRuleStatus.FAILED) {
          // Failed
          console.log('Travel rule status check failed with status:', data.status);
          statusCheckTimerRef.current = null;
          setIsProcessing(false);
          setCompletionStatus('failed');
        } else if (data.status === TravelRuleStatus.SENT || data.status === TravelRuleStatus.NEEDED) {
          // If still in SENT or NEEDED status, check again with backoff
          timerGetTravelRuleStatus();
        }
      }).catch((error) => {
        console.error('Error fetching travel rule status:', error);
        // Retry on error with backoff instead of stopping
        timerGetTravelRuleStatus();
      });
    }, delay);
  };

  // Cleanup timer on component unmount
  useEffect(() => {
    return () => {
      if (statusCheckTimerRef.current) {
        clearTimeout(statusCheckTimerRef.current);
      }
    };
  }, []);

  const messageHandler = async (message: unknown) => {
    if (message == 'idCheck.onStepCompleted') {
      // Show processing state
      setIsProcessing(true);
      // Reset attempt counter for new flow
      attemptCountRef.current = 0;
      // Submit the applicant for review (WebSDK does not auto-submit questionnaire-only levels)
      try {
        const type = isDeposit ? 'deposit' : 'withdrawal';
        await SumsubService.submitTravelRuleApplicant(type);
      } catch (e) {
        console.error('Error submitting travel rule applicant:', e);
      }
      // Start checking the travel rule status every 30 seconds
      timerGetTravelRuleStatus();
    }
  };

  const errorHandler = (error: unknown) => {
    console.error('Error from Sumsub WebSDK:', error);
  };

  const config = {
    lang: (i18n?.language || "en")
  };

  const options = {};

  const renderCompletionScreen = () => (
    <div className="dv-form__guide">
      <div className="general-modal__content" style={{ textAlign: 'center' }}>
        <p style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: '0.5rem' }}>
          {completionStatus === 'completed'
            ? t('message.travel_rule_completed')
            : t('message.travel_rule_failed')}
        </p>
      </div>
      <div className="general-modal__footer">
        <button
          className="cta-button cta-medium cta-blue"
          onClick={onClose}
        >
          <span>{t('common.close')}</span>
        </button>
      </div>
    </div>
  );

  if (isFullModal) return (
    <div className="general-modal" style={{ width: "100%", maxWidth: 750, maxHeight: "100%" }}>
      {completionStatus ? (
        renderCompletionScreen()
      ) : isProcessing ? (
        <div className="dv-form__guide">
          <div className="general-modal__header">
            {t('common.processing')}
          </div>
          <div className="general-modal__content" style={{ textAlign: 'center' }}>
            <Loading />
            <p style={{ marginTop: '1rem' }}>
              {t('message.travel_rule_processing')}
            </p>
          </div>
        </div>
      ) : !showWebSdk ? (
        <div className="dv-form__guide">
          <div className="general-modal__header">
            {t('common.complementary_info')}
          </div>
          <div className="general-modal__content">
            <p>
              {t('travel-rule.description_1')} {' '}
              {isDeposit ? t('travel-rule.deposit') : t('travel-rule.withdraw')} {' '}
              {t('travel-rule.description_2')}
            </p>
            <p>
              {t('travel-rule.enter_info')}
            </p>
          </div>
          <div className="general-modal__footer">
            <button
              className="cta-button cta-medium cta-blue"
              onClick={handleNext}
              disabled={isStatusLoading}
            >
              <span>{t('common.next')}</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="custom-scrollbar" style={{ maxHeight: "600px" }}>
          { loading ? (
            <Loading />
          ) : (
            <SumsubWebSdk
              accessToken={accessToken}
              expirationHandler={accessTokenExpirationHandler}
              config={config}
              options={options}
              onMessage={messageHandler}
              onError={errorHandler}
            />
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      {completionStatus ? (
        renderCompletionScreen()
      ) : isProcessing ? (
        <div className="dv-form__guide">
          <div className="general-modal__content" style={{ textAlign: 'center' }}>
            <Loading />
            <p style={{ marginTop: '1rem' }}>
              {t('message.travel_rule_processing')}
            </p>
          </div>
        </div>
      ) : !showWebSdk ? (
        <div className="dv-form__guide">
          <div className="general-modal__content">
            <p>
              {t('travel-rule.description_1')} {' '}
              {isDeposit ? t('travel-rule.deposit') : t('travel-rule.withdraw')} {' '}
              {t('travel-rule.description_2')}
            </p>
            <p>
              {t('travel-rule.enter_info')}
            </p>
          </div>
          <div className="general-modal__footer">
            <button
              className="cta-button cta-medium cta-blue"
              onClick={handleNext}
              disabled={isStatusLoading}
            >
              <span>{t('common.next')}</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="custom-scrollbar" style={{ maxHeight: "600px" }}>
          { loading ? (
            <Loading />
          ) : (
            <SumsubWebSdk
              accessToken={accessToken}
              expirationHandler={accessTokenExpirationHandler}
              config={config}
              options={options}
              onMessage={messageHandler}
              onError={errorHandler}
            />
          )}
        </div>
      )}
    </>
  );
};

export default TravelRuleModal;
